/**
 * Import a Studio package (a zip made by an AI agent or by hand) as a new project, or rebuild an
 * existing project from a newer version of it. "Check" lists every problem without importing; the
 * instructions, schema and example are for whoever builds it.
 */

import { Download, FileArchive, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, ApiError, type Job, type PackageCheck, type ProjectSummary } from "./api";
import { appUrl } from "./base";

export function PackageImport({ onClose, onOpen }: { onClose: () => void; onOpen: (id: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<PackageCheck | null>(null);
  const [busy, setBusy] = useState<{ what: string; job?: Job } | null>(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState<{ id: string; warnings: string[]; updated: boolean; shared: number } | null>(null);
  const [into, setInto] = useState("");
  const [toLibrary, setToLibrary] = useState(false);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  useEffect(() => { api.projects().then(setProjects).catch(() => {}); }, []);

  const check = async () => {
    if (!file) return;
    setErr("");
    setBusy({ what: "Checking" });
    try {
      setResult(await api.checkPackage(file));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const run = async () => {
    if (!file) return;
    setErr("");
    setBusy({ what: "Uploading" });
    try {
      let job = await api.importPackage(file, { into: into || undefined, library: toLibrary });
      while (["queued", "running"].includes(job.status)) {
        setBusy({ what: "Importing", job });
        await new Promise((r) => setTimeout(r, 800));
        job = await api.job(job.id);
      }
      if (job.status !== "done") throw new Error(job.message || "import failed");
      const out = job.output as { projectId: string; warnings: string[]; updated?: boolean; shared?: number };
      setDone({ id: out.projectId, warnings: out.warnings, updated: !!out.updated, shared: out.shared ?? 0 });
    } catch (e) {
      const d = e instanceof ApiError ? (e.body as { problems?: string[] } | undefined) : undefined;
      if (d?.problems) setResult({ ok: false, problems: d.problems, warnings: [], summary: null });
      else setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modal-back" onPointerDown={onClose}>
      <div className="modal package" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Import package</h3>
          <button className="icon" onClick={onClose}><X size={18} /></button>
        </header>
        <p className="muted small">
          A package is one .zip with a <code>studio.json</code> manifest and the images, audio and fonts it uses. It becomes a new project with its scenes,
          layers, voiceover timing and captions already set up. To have Claude build one anywhere, add the <strong>Claude skill</strong> to that session (it includes the
          instructions, the schema, an example and a checker); the separate files are below too.
        </p>
        <div className="pkg-links">
          <a className="button primary" href={appUrl("/api/packages/skill.zip")} title="Add to any Claude session: claude.ai → Settings → Capabilities → Skills → Upload, or unzip into ~/.claude/skills for Claude Code">
            <Download size={13} /> Claude skill (.zip)
          </a>
          <a className="button" href={appUrl("/api/packages/instructions.md")} target="_blank" rel="noreferrer"><Download size={13} /> Instructions (.md)</a>
          <a className="button" href={appUrl("/api/packages/schema.json")} download="package.schema.json"><Download size={13} /> Schema (.json)</a>
          <a className="button" href={appUrl("/api/packages/example.zip")}><Download size={13} /> Example package</a>
        </div>
        <div className="pkg-pick">
          <label className="button">
            <FileArchive size={14} /> {file ? file.name : "Choose a .zip"}
            <input type="file" accept=".zip,application/zip" hidden
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setDone(null); setErr(""); e.target.value = ""; }} />
          </label>
          <span className="grow" />
          <button disabled={!file || !!busy} onClick={check} title="List any problems without importing">Check</button>
          <button className="primary" disabled={!file || !!busy || result?.ok === false || !!done} onClick={run}>{into ? "Update project" : "Import"}</button>
        </div>
        <div className="pkg-opts">
          <label>
            Import as
            <select value={into} onChange={(e) => { setInto(e.target.value); setDone(null); }}>
              <option value="">a new project</option>
              <optgroup label="an update to">
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            </select>
          </label>
          <label title="Images, audio and fonts from the package go into the shared library, tagged with the project name. Files already there are not added twice.">
            <input type="checkbox" checked={toLibrary} onChange={(e) => setToLibrary(e.target.checked)} /> Also add its files to the shared library
          </label>
        </div>
        {into && (
          <p className="muted small">
            The package replaces this project's scenes, layers, audio, camera and captions. Files it already has are reused (keeping their tracing); its other
            files stay. The current version is kept in the project's history, so you can go back to it.
          </p>
        )}
        {busy && (
          <p className="muted small"><Loader2 size={13} className="spin" /> {busy.job?.message && busy.job.message !== "starting" ? busy.job.message : busy.what}
            {busy.job?.status === "queued" ? " (waiting for the worker)" : busy.job ? ` ${Math.round(busy.job.progress * 100)}%` : "…"}</p>
        )}
        {err && <p className="bad small">{err}</p>}
        {result && !result.ok && (
          <div className="pkg-report bad-box">
            <strong>{result.problems.length} problem{result.problems.length === 1 ? "" : "s"} to fix:</strong>
            <ul>{result.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
          </div>
        )}
        {result?.ok && result.summary && (
          <div className="pkg-report good-box">
            <strong>Ready to import:</strong> “{result.summary.name}”, {result.summary.scenes} scenes, {result.summary.layers} layers,
            {" "}{result.summary.audio} audio files, {result.summary.cues} cue phrases, {result.summary.files} files.
          </div>
        )}
        {(done?.warnings ?? result?.warnings ?? []).length > 0 && (
          <div className="pkg-report">
            <strong>Warnings</strong>
            <ul>{(done?.warnings ?? result!.warnings).map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
        {done && (
          <div className="analysis-actions" style={{ justifyContent: "flex-start" }}>
            <span className="good small">
              {done.updated ? "Updated." : "Imported."} {done.shared > 0 && `${done.shared} file${done.shared === 1 ? "" : "s"} added to the shared library. `}
              New images and fonts are being analysed in the background.
            </span>
            <button className="primary" onClick={() => onOpen(done.id)}>Open the project</button>
          </div>
        )}
      </div>
    </div>
  );
}
